/**
 * Picker shown when a user clicks "Add Essential" inside a project room.
 *
 * Two tabs:
 *   • Choose existing — search bar + items grouped by category. Notes shown
 *     as a small caption / tooltip so users know what each one is.
 *   • Custom — name input → create one-off essential not in the library.
 *
 * Picking calls `onPick(name)` with the name string; the caller is
 * responsible for the actual addItem API call (so the dialog stays
 * project-agnostic and reusable).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Search, Link2, StickyNote, Plus, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  getFurnitureEssentialLinks,
  getFurnitureEssentialCategories,
} from '@/lib/api';

export function EssentialPickerDialog({ open, onOpenChange, onPick }) {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('existing');
  const [search, setSearch] = useState('');
  const [customName, setCustomName] = useState('');
  const [picking, setPicking] = useState(false);

  // Track which essentials were picked in the current dialog session so
  // we can give the user visual confirmation. Cleared each time the dialog
  // opens.
  const [pickedSet, setPickedSet] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setCustomName('');
    setTab('existing');
    setPickedSet(new Set());
    setLoading(true);
    Promise.all([getFurnitureEssentialLinks(), getFurnitureEssentialCategories()])
      .then(([es, cs]) => { setEntries(es); setCategories(cs); })
      .catch(() => toast.error('Could not load essentials'))
      .finally(() => setLoading(false));
  }, [open]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const visible = q
      ? entries.filter(e => e.name.toLowerCase().includes(q) || (e.notes || '').toLowerCase().includes(q))
      : entries;

    const known = new Map();
    categories.forEach(c => known.set(c.name, []));
    const orphans = new Map();
    const uncat = [];
    for (const e of visible) {
      const cat = e.category;
      if (!cat) { uncat.push(e); continue; }
      if (known.has(cat)) known.get(cat).push(e);
      else {
        if (!orphans.has(cat)) orphans.set(cat, []);
        orphans.get(cat).push(e);
      }
    }
    const out = categories
      .map(c => ({ key: c.id, name: c.name, items: known.get(c.name) || [] }))
      .filter(g => g.items.length > 0);
    [...orphans.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([name, items]) => {
      out.push({ key: `orphan-${name}`, name, items });
    });
    if (uncat.length) out.push({ key: 'uncat', name: 'Uncategorized', items: uncat });
    for (const g of out) g.items.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [entries, categories, search]);

  const handlePick = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setPicking(true);
    try {
      await onPick(trimmed);
      // Stay open so the user can keep adding. Show a small confirmation,
      // mark the row as picked, and clear the custom-tab input.
      setPickedSet(prev => {
        const next = new Set(prev);
        next.add(trimmed.toLowerCase());
        return next;
      });
      setCustomName('');
      toast.success(`Added: ${trimmed}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add');
    } finally {
      setPicking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="essential-picker-dialog">
        <DialogHeader>
          <DialogTitle>Add essential</DialogTitle>
          <DialogDescription>
            Pick from your library or add a custom one. The dialog stays open so you can add several in a row — close it when you're done.
            {pickedSet.size > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 text-emerald-700 font-medium">
                · {pickedSet.size} added this session
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="self-start">
            <TabsTrigger value="existing" data-testid="ess-picker-tab-existing">Choose existing</TabsTrigger>
            <TabsTrigger value="custom" data-testid="ess-picker-tab-custom">Custom</TabsTrigger>
          </TabsList>

          <TabsContent value="existing" className="flex-1 flex flex-col min-h-0 mt-3">
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9ca3af]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search essentials, categories, or notes…"
                className="pl-8 h-9 text-sm"
                autoFocus
                data-testid="ess-picker-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto pr-1 -mr-1">
              {loading ? (
                <div className="py-8 text-center text-sm text-[#9ca3af]">
                  <Loader2 className="h-4 w-4 inline animate-spin mr-1" /> Loading…
                </div>
              ) : grouped.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#9ca3af]">
                  {search ? 'No matches.' : 'Your essentials library is empty.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {grouped.map(group => (
                    <div key={group.key}>
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-[#5b6773] px-1 mb-1">
                        {group.name} <span className="text-[#9ca3af]">({group.items.length})</span>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {group.items.map(entry => {
                          const wasPicked = pickedSet.has(entry.name.toLowerCase());
                          return (
                            <button
                              key={entry.name_key}
                              type="button"
                              disabled={picking}
                              onClick={() => handlePick(entry.name)}
                              className={`text-left flex items-center gap-2 px-2 py-1.5 rounded disabled:opacity-50 group ${wasPicked ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-[#2d948c]/10'}`}
                              title={entry.notes || ''}
                              data-testid={`ess-picker-item-${entry.name_key.replace(/\s+/g, '-')}`}
                            >
                              <span className={`text-[13px] flex-1 truncate ${wasPicked ? 'text-emerald-800' : 'text-[#1f2933]'}`}>
                                {entry.name}
                              </span>
                              {entry.links?.length > 0 && (
                                <Link2 className="h-3 w-3 text-emerald-700/80 flex-shrink-0" title={`${entry.links.length} link${entry.links.length === 1 ? '' : 's'}`} />
                              )}
                              {entry.notes?.trim() && (
                                <StickyNote className="h-3 w-3 text-[#5b6773]/70 flex-shrink-0" title={entry.notes} />
                              )}
                              {wasPicked ? (
                                <Check className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0" />
                              ) : (
                                <Plus className="h-3.5 w-3.5 text-[#2d948c] opacity-0 group-hover:opacity-100" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="custom" className="mt-3">
            <div className="space-y-2">
              <p className="text-xs text-[#5b6773]">
                Add a one-off essential by name. It will be saved to your library too, so you can reuse it later.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePick(customName); } }}
                  placeholder="e.g. Bath Mat"
                  autoFocus
                  data-testid="ess-picker-custom-name"
                />
                <Button onClick={() => handlePick(customName)} disabled={!customName.trim() || picking} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ess-picker-custom-add">
                  Add
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ess-picker-done">
            {pickedSet.size > 0 ? `Done (${pickedSet.size} added)` : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
